/**
 * Constants taken verbatim from the original engine.
 *
 * Every value here is cited to the original source under `reference/GameplayFootball/`.
 * These are load-bearing: the game's feel is the product of these numbers tuned against
 * each other. Do not "clean up" or round them. See docs/PLAN.md §6.
 */

// ---------------------------------------------------------------------------
// Timing  (gamedefines.hpp, match.cpp)
// ---------------------------------------------------------------------------

/** Simulation step. Fixed. The original never scales anything by measured delta time. */
export const TICK_MS = 10;

/**
 * How far behind the simulation the renderer draws, interpolated.
 * `gamedefines.hpp:68` — temporalSmoother_history_ms.
 */
export const RENDER_LAG_MS = 20;

/**
 * Ring buffer depth for interpolated values.
 * `utils.cpp:613` — `3 + ceil(history_ms / 10)`. Reproduced as a formula, not a literal.
 */
export const SMOOTHER_SAMPLES = 3 + Math.ceil(RENDER_LAG_MS / TICK_MS); // = 5

/**
 * Upper bound on simulation catch-up per rendered frame.
 *
 * DIVERGENCE FROM ORIGINAL, deliberate. The original's game sequence catches up without
 * limit after an overrun and never skips ticks. In a browser, one tab stall would then
 * spiral: the longer it takes to catch up, the more there is to catch up. We cap it and
 * let the match clock slip instead. See docs/PLAN.md §3.1.
 */
export const MAX_CATCHUP_TICKS = 5;

/**
 * Display clock rate.  `match.cpp:52,963`
 *   matchDurationFactor = match_duration * 0.2 + 0.05
 *   matchTime_ms += 10 * (1 / matchDurationFactor)   // only while in play, not in a set piece
 *
 * With the config default of match_duration = 1.0 the factor is 0.25, so the displayed
 * clock runs 4x the simulation clock. The menu can set other values.
 */
export const matchDurationFactor = (matchDuration: number): number =>
  matchDuration * 0.2 + 0.05;

// ---------------------------------------------------------------------------
// Ball  (ball.cpp:23-29, and radius at ball.cpp:93,233)
// ---------------------------------------------------------------------------

export const BALL = {
  /** 1 = perfectly elastic, 0 = no bounce. */
  bounce: 0.62,
  /** Constant velocity subtracted after a bounce. Bigger = more brake. */
  linearBounce: 0.06,
  /** Quadratic air drag coefficient. */
  drag: 0.015,
  /** Quadratic ground friction. */
  friction: 0.04,
  /** Linear ground friction, arbitrary scale. */
  linearFriction: 1.6,
  gravity: -9.81,
  /** Depth of the grass layer the ball sinks into. */
  grassHeight: 0.025,
  radius: 0.11,
} as const;

/**
 * Forward-simulation horizon.  `gamedefines.hpp:56` — ballPredictionSize_ms.
 *
 * Every tick the ball simulates this far ahead into an array; the live ball is entry 0 and
 * every AI question about the future is an O(1) lookup. A touch re-runs it immediately, so
 * a kick is visible to all AI within the same tick. This is the AI's entire information
 * model about the ball, and the suspected dominant CPU cost.
 */
export const BALL_PREDICTION_MS = 3000;
export const BALL_PREDICTION_STEPS = BALL_PREDICTION_MS / TICK_MS; // = 300

// ---------------------------------------------------------------------------
// Pitch  (gamedefines.hpp:271-277)
// ---------------------------------------------------------------------------

export const PITCH = {
  /** Half the goal-to-goal distance, inside the lines. Full pitch is 110 m long. */
  halfLength: 55.0,
  /** Half the touchline-to-touchline distance, inside the lines. Full pitch is 72 m wide. */
  halfWidth: 36.0,
  /** Including the surrounding rim of grass outside the lines. */
  fullHalfLength: 60.0,
  fullHalfWidth: 40.0,
  lineHalfWidth: 0.06,
  goalDepth: 2.55,
  goalHeight: 2.5,
  goalHalfWidth: 3.7,
} as const;

// ---------------------------------------------------------------------------
// Coordinate system
// ---------------------------------------------------------------------------

/**
 * THE ORIGINAL IS Z-UP. Three.js is Y-up. This is the single most likely source of
 * silent, maddening bugs in this project, so the rule is fixed here once:
 *
 *   The SIMULATION works entirely in the original's Z-up frame.
 *   Conversion to Y-up happens ONLY at the render boundary.
 *
 * Why this way round: every physics constant, every AI formula and every angle in
 * docs/original-engine/ is written in the original's frame. Porting them literally means
 * they can be read side by side with the C++ and checked. If the simulation were Y-up,
 * every ported line would need a mental axis swap and the errors would be invisible.
 *
 * In the original's frame:
 *   x — along the pitch, goal to goal   (±55 inside the lines)
 *   y — across the pitch, touchline to touchline (±36)
 *   z — up. The ball rests at z = BALL.radius (ball.cpp:93,197,204).
 */
export const SIM_UP_AXIS = 'z' as const;
